import type { FilterOption } from './types';

// https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
function stripDiacritics(string: string) {
  return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * A custom implementation of MUI's createFilterOptions that handles matching parent/child
 * options correctly.
 *
 * @see https://github.com/mui/material-ui/blob/c172f6da6ed20f967ded436f29695e5c23be9dd5/packages/mui-material/src/useAutocomplete/useAutocomplete.js#L17
 */
export function filterOptions(
  options: FilterOption[],
  {
    inputValue,
    getOptionLabel,
  }: { inputValue: string; getOptionLabel: (option: FilterOption) => string },
) {
  const input = stripDiacritics(inputValue.toLowerCase());

  if (!input) {
    return options;
  }

  const optionMatchesInput = (option: FilterOption) => {
    const candidate = stripDiacritics(getOptionLabel(option).toLowerCase());
    return candidate.includes(input);
  };

  return options.filter((option) => {
    const thisOptionMatches = optionMatchesInput(option);
    const parentMatches = !!option.parent && optionMatchesInput(option.parent);
    const childMatches = option.children?.some(optionMatchesInput);

    return thisOptionMatches || parentMatches || childMatches;
  });
}

export function getCheckboxState(
  option: FilterOption,
  selectedValues: Set<string>,
) {
  const { value, children = [] } = option;

  if (!children.length) {
    return {
      checked: selectedValues.has(value),
      indeterminate: false,
    };
  }

  const someChildrenSelected = children.some(({ value: childValue }) =>
    selectedValues.has(childValue),
  );
  const allChildrenSelected = children.every(({ value: childValue }) =>
    selectedValues.has(childValue),
  );

  return {
    checked: allChildrenSelected,
    indeterminate: someChildrenSelected && !allChildrenSelected,
  };
}

export function getOptionsToToggle(
  option: FilterOption,
  isSelected: boolean,
  selectedValues: Set<string>,
) {
  const { children = [] } = option;

  if (!children.length) {
    return [option];
  }

  const optionsToToggle = children.filter(({ value: childValue }) => {
    if (isSelected) {
      return selectedValues.has(childValue);
    }
    return !selectedValues.has(childValue);
  });

  return optionsToToggle;
}
